// src/features/logs/LogsPage.tsx
import { useState } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { useLogs, type LogEntry } from './useLogs';
import { summarize } from './summarize';
import { formatRelative, formatExactDateTime } from '../../lib/formatTime';
import './LogsPage.css';

function LogRow({ entry, index }: { entry: LogEntry; index: number }) {
  return (
    <tr className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
      <td className="logs-table__time">
        {formatRelative(entry.created_at)}
        <span className="logs-table__exact-time"> · {formatExactDateTime(entry.created_at)}</span>
      </td>
      <td className="logs-table__user">{entry.user_display_name ?? entry.user_id}</td>
      <td>{summarize(entry)}</td>
    </tr>
  );
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];

export function LogsPage() {
  const { selectedGuildId } = useGuild();
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const { data, isLoading, error } = useLogs(selectedGuildId, offset, limit);

  const total = data?.total ?? 0;
  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  function handleLimitChange(newLimit: number) {
    setLimit(newLimit);
    setOffset(0); // changing page size while mid-way through pages would show a confusing mismatched range otherwise
  }

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Dashboard change history</p>
        <h1 className="page-header__title">Logs</h1>
      </div>

      <article className="card">
        <header className="card__header logs-card__header">
          <div>
            <p className="card__eyebrow">{total} total entries</p>
            <h2 className="card__title">Recent changes</h2>
          </div>
          <label className="logs-per-page">
            Per page
            <select className="input" value={limit} onChange={(e) => handleLimitChange(Number(e.target.value))}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </header>
        <div className="card__content">
          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load logs. Try refreshing.</p>}
          {data?.entries.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No changes recorded yet for this server.</p>}

          {data && data.entries.length > 0 && (
            <>
              <table className="logs-table">
                <thead>
                  <tr><th>When</th><th>Who</th><th>What changed</th></tr>
                </thead>
                <tbody>
                  {data.entries.map((entry, index) => <LogRow key={entry.id} entry={entry} index={index} />)}
                </tbody>
              </table>

              <div className="logs-pagination">
                <button className="button-secondary" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={!hasPrev}>
                  Newer
                </button>
                <span className="logs-pagination__label">
                  {offset + 1}–{Math.min(offset + limit, total)} of {total}
                </span>
                <button className="button-secondary" onClick={() => setOffset(offset + limit)} disabled={!hasNext}>
                  Older
                </button>
              </div>
            </>
          )}
        </div>
      </article>
    </>
  );
}
